/**
 * Module to show suggestions on typing. a simple, autocomplete module for modern browsers
 * @author Serge Huijben
 *
 * MIT license
 */
import { registerTriggers, triggerNotify } from "../../@helpers/js/observer.js";

class SearchSuggestions {
  constructor(
    element,
    {
      autosuggestUrl = "",
      method = "GET",
      key = "key",
      autosuggestKey = "q",
      subtitleprefix = "Nr:",
      autofocus = false,
      sort = false,
      filter = false,
      minChars = 3,
      maxItems = 15,
      autoFirst = false,
    } = {}
  ) {
    this._options = {
      autosuggestUrl,
      method,
      key,
      autosuggestKey,
      subtitleprefix,
      autofocus,
      sort,
      filter,
      minChars,
      maxItems,
      autoFirst,
    };
    this._element = element;
    this.input = this._element.querySelector("input.c-search-suggestions__input");
    this.submitButton = this._element.querySelector("[data-search-suggestions-button]"); // use this attribute only if you want autosuggest on button click
    this.notFoundTemplate = this._element.querySelector("[data-search-suggestions-404]");
    this.overMaxFoundTemplate = this._element.querySelector("[data-search-suggestions-over-max]");

    if (this.input) {
      this._init();
    }
  }

  // Setup
  _init() {
    this.input.setAttribute("autocomplete", "off");
    this.input.setAttribute("aria-autocomplete", "list");
    this.input.value = getQueryString(this._options.key);

    this.index = -1;

    this.filter = (text, input, filter) => filter ? new RegExp(regExpEscape(input.trim()), "i").test(text) : true;

    this.sort = (a, b, value) => {
      if (value && isNaN(value)) {
        const val = regExpEscape(value);
        let aio = a.naam.toLowerCase().indexOf(val.toLowerCase());
        let bio = b.naam.toLowerCase().indexOf(val.toLowerCase());

        if (aio < 0) {
          let areg = new RegExp(val.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""), "g");
          aio = areg.test(a.normalizedName) ? areg.lastIndex - val.length + 99
            : a.length + 199;
        }
        if (bio < 0) {
          let breg = new RegExp(val.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""), "g");
          bio = breg.test(b.normalizedName) ? breg.lastIndex - val.length + 99 : b.length + 199;
        }
        return aio - bio;
      } else if (value) {
        let anio = a.relatienummer.length - a.relatienummer.indexOf(value) + a.relatienummer.replace(/^0+/, "").indexOf(value);
        let bnio = b.relatienummer.length - b.relatienummer.indexOf(value) + b.relatienummer.replace(/^0+/, "").indexOf(value);
        return anio - bnio;
      }
      return 0;
    };

    this._createElements();

    this._bindEvents();

    this.list = [];

    registerTriggers([
      "searchsuggestions-highlight",
      "searchsuggestions-close",
      "searchsuggestions-open",
      "searchsuggestions-select",
      "searchsuggestions-selectcomplete",
    ]);

    if (this.input.value && this._options.autofocus === true)
      window.setTimeout(() => this.input.focus(), 100);
  }

  // Create necessary elements
  _createElements() {
    this.container = document.createElement("div");
    this.container.className = "suggestions";
    this.input.parentNode.insertBefore(this.container, this.input);
    this.container.appendChild(this.input);

    this.innerContainer = document.createElement("div");
    this.innerContainer.className = "c-search-suggestions__list";
    this.innerContainer.setAttribute("hidden", "hidden");
    this.container.appendChild(this.innerContainer);

    this.innerContainer.innerHTML = "";

    this.ul = document.createElement("ul");
    this.innerContainer.appendChild(this.ul);

    this.notFound = document.createElement("div");
    this.notFound.className = "c-search-suggestions__list-404";
    this.innerContainer.appendChild(this.notFound);

    this.status = document.createElement("span");
    this.status.className = "t-visuallyhidden";
    this.status.setAttribute("role", "status");
    this.status.setAttribute("aria-live", "assertive");
    this.status.setAttribute("aria-atomic", "true");
    this.status.setAttribute("aria-relevant", "additions");
    this.container.appendChild(this.status);
  }

  // Bind events
  _bindEvents() {
    if (this.input) {
      ["click", "touchend", "focus"].forEach((event) => {
        this.input.addEventListener(event, () => {
          if (this.timeoutID && typeof this.timeoutID === "number") {
            window.clearTimeout(this.timeoutID);
          }
          this.timeoutID = window.setTimeout(() => {
            if (this.inputValueBackup && this.input.value && this.input.value === this.inputValueBackup &&
              ((this.notFoundBackup && this.notFoundBackup.length > 0) || (this.ulBackup && this.ulBackup.length > 0))
            ) {
              this.ul.innerHTML = this.ulBackup;
              this.notFound.innerHTML = this.notFoundBackup;
              this.open();
            } else if (!this.input.value || this.input.value.length < this._options.minChars) {
              this.close({ reason: "clear" });
            } else if (this.inputValueBackup && this.input.value && this.input.value.toLowerCase().startsWith(this.inputValueBackup.toLowerCase())) {
              this._element.classList.add("loading");
              this.showSuggestions(this.input.value, true);
            } else {
              this._element.classList.add("loading");
              this.evaluate();
            }
          }, 200);
        });
      });
      // Act on keypress and paste action for the following keys:
      // < > ' " $ { } [ ] /  keys
      const forbiddenChars = /[<>{}[\]$"'/]/g;
      this.input.addEventListener("input", () => {
        this.input.value = this.input.value.replace(forbiddenChars, "");
        if (this.inputValueBackup && this.input.value && this.input.value.toLowerCase().startsWith(this.inputValueBackup.toLowerCase())) {
          return this.showSuggestions(this.input.value, true);
        }
        return this.wrapEvaluation();
      });
      this.input.addEventListener("blur", () => this.close({ reason: "blur" }));
      this.input.addEventListener("keydown", (evt) => {
        const c = evt.keyCode;

        // If the dropdown `ul` is in view, then act on keydown for the following keys:
        // Enter / Esc / Up / Down
        if (this.opened) {
          if (c === 13) {
            // Enter
            if (this.selected) {
              evt.preventDefault();
              this.select();
            } else {
              if (this.submitButton) {
                this.submitButton.click();
              } else {
                this.close({
                  reason: "non-select",
                });
                triggerNotify("searchsuggestions-selectcomplete");
              }
            }
          } else if (c === 27) {
            // Esc
            this.close({
              reason: "esc",
            });
          } else if (c === 38 || c === 40) {
            // Down/Up arrow
            evt.preventDefault();
            let count = this.ul.children.length;
            if (count <= 0) {
              let linkToSearch = this.notFound.querySelector(".c-link");
              if (linkToSearch) {
                let toggle =
                  linkToSearch.getAttribute("aria-selected") &&
                  linkToSearch.getAttribute("aria-selected") === "true";
                linkToSearch.setAttribute("aria-selected", !toggle);
              }
            } else {
              this[c === 38 ? "previous" : "next"](count);
            }
          }
        } else {
          if (c === 13) {
            // Enter
            if (this.submitButton) {
              this.submitButton.click();
            } else {
              triggerNotify("searchsuggestions-selectcomplete");
            }
          }
        }
      });
   }

    if (this.submitButton) {
      this.submitButton.addEventListener("click", (evt) => {
        evt.preventDefault();
        this._element.classList.add("loading");
        this.evaluate(true);
      });
    }

    if (this.input && this.input.form) {
      this.input.form.addEventListener("submit", () =>
        this.close({ reason: "submit" })
      );
    }

    if (this.ul) {
      this.ul.addEventListener("mousedown", (evt) => {
        let li = evt.target;

        if (li !== this) {
          while (li && !/li/i.test(li.nodeName)) {
            li = li.parentNode;
          }

          if (li && evt.button === 0) {
            // Only select on left click
            evt.preventDefault();
            this.select(li, evt.target);
          }
        }
      });
    }
  }

  set list(list) {
    if (typeof list === "string" && list.indexOf(",") > -1) {
      this._list = list.split(/\s*,\s*/);
    } else {
      // Element or CSS selector
      this._list = list;
    }

    if (document.activeElement === this.input) {
      this.wrapEvaluation();
    }
  }

  get list() {
    return this._list;
  }

  get selected() {
    return this.index > -1;
  }

  get opened() {
    return !this.innerContainer.hasAttribute("hidden");
  }

  close(o) {
    if (!this.opened) {
      return;
    }
    const ms = o && o.reason && o.reason === "clear" ? 10 : 200;
    window.setTimeout(() => {
      this.innerContainer.setAttribute("hidden", "");
      this.notFound.innerHTML = "";
      this.ul.innerHTML = "";
    }, ms);
    this.index = -1;

    triggerNotify("searchsuggestions-close", o || {});
  }

  open() {
    this.innerContainer.removeAttribute("hidden");

    if (this._options.autoFirst && this.index === -1) {
      this.goto(0);
    }

    triggerNotify("searchsuggestions-open");
  }

  next(count) {
    this.goto(this.index < count - 1 ? this.index + 1 : -1);
  }

  previous(count) {
    this.goto(this.selected ? this.index - 1 : count - 1);
  }

  // Should not be used, highlights specific item without any checks!
  goto(i) {
    const lis = this.ul.children;

    if (this.selected) {
      lis[this.index].setAttribute("aria-selected", "false");
    }

    this.index = i;

    if (i > -1 && lis.length > 0) {
      lis[i].setAttribute("aria-selected", "true");
      this.status.textContent = lis[i].textContent;

      triggerNotify(
        "searchsuggestions-highlight",
        this.suggestions[this.index]
      );
    }
  }

  select(selected, origin) {
    if (selected) {
      this.index = siblingIndex(selected);
    } else {
      selected = this.ul.children[this.index];
    }

    if (selected) {
      const suggestion = this.suggestions[this.index];
      this.input.value = suggestion.relatienummer;
      this.inputValueBackup = this.input.value;
      this.close({
        reason: "select",
      });
      triggerNotify("searchsuggestions-selectcomplete", suggestion);
    }
  }

  wrapEvaluation() {
    if (this.timeoutID && typeof this.timeoutID === "number") {
      window.clearTimeout(this.timeoutID);
    }
    this.timeoutID = window.setTimeout(() => {
      this._element.classList.add("loading");
      this.evaluate();
    }, 400);
  }

  noSuggestionsFound(value) {
    if (this.notFoundTemplate && this.notFoundTemplate.innerHTML && this.notFoundTemplate.innerHTML.length > 0) {
      this.notFound.innerHTML = this.notFoundTemplate.innerHTML.replace(/\{0\}/g, value);
      return true;
    }
    return false;
  }

  moreMaxSuggestionsFound(value) {
    if (this.overMaxFoundTemplate && this.overMaxFoundTemplate.innerHTML && this.overMaxFoundTemplate.innerHTML.length > 0) {
      this.notFound.innerHTML = this.overMaxFoundTemplate.innerHTML.replace(/\{0\}/g, this._options.maxItems).replace(/\{1\}/g, value);
      return true;
    }
    return false;
  }

  async evaluate(skipLengthCheck) {
    const value = this.input.value;
    const controller = window.AbortController ? new AbortController() : {};
    const signal = controller.signal || {};
    
    let fetchPromise;
    if ((skipLengthCheck && value.length > 1) || value.length >= this._options.minChars) {
      if (this._options.autosuggestUrl) {
        this.inputValueBackup = "";
        this.notFound.innerHTML = "";
        this.ul.innerHTML = "";
        if (fetchPromise && controller.abort) {
          controller.abort();
        }
        let suggestURL = "";
        let searchData = {};
        let key = this._options.key ? this._options.key : "key";
        if (this._options.method === "POST") {
          suggestURL = this._options.autosuggestUrl;

          // Create data object
          searchData = {
            maxResults: this._options.maxResults,
          };
          // Add key value pair to data object
          searchData[key] = value;
        } else {
          suggestURL = `${this._options.autosuggestUrl}&${this._options.autosuggestKey}=${value}`;
        }
        try {
          fetchPromise = fetch(suggestURL, {
            signal,
            method: this._options.method || "GET",
            headers: {
              "Content-Type": "application/json; charset=utf-8",
            },
            body: this._options.method === "POST" ? JSON.stringify(searchData) : undefined,
          });
          let response = await fetchPromise;
          let result = await response.json();

          if (result && result.isSucces && result.data) {
            this._list = result.data.collectiviteiten;
            this.showSuggestions(value);
          } else {
            this._element.classList.remove("loading");
            if (this.noSuggestionsFound(value)) {
              this.open();
            } else {
              this.close({
                reason: "nomatches",
              });
            }
          }
        } catch (error) {
          this._element.classList.remove("loading");
          console.error(error);
          if (this.noSuggestionsFound(value)) {
            this.open();
          } else {
            this.close({
              reason: "error",
            });
          }
        } finally {
          fetchPromise = undefined;
          if (this.input.value && this.input.value.length >= 2) {
            this.inputValueBackup = `${this.input.value}`;
            this.notFoundBackup = `${this.notFound.innerHTML}`;
            this.ulBackup = `${this.ul.innerHTML}`;
          }
        }
      } else {
        this._element.classList.remove("loading");
        this.close({
          reason: "no autosuggest url",
        });
      }
    } else {
      this._element.classList.remove("loading");
      this.close({
        reason: "to little characters",
      });
    }
  }

  showSuggestions(value, filter) {
    if (this._list.length > 0) {
      this.index = -1;
      // Populate list with options that match
      const collSuggestions = this._list
        .map((item) => new collectiviteit(item))
        .filter((item) => {
          return this.filter(isNaN(value) ? item.normalizedName : item.relatienummer, value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""), filter);
        })
        .sort((a, b) => this._options.sort ? a.normalizedName < b.normalizedName ? -1 : a.normalizedName > b.normalizedName ? 1 : 0 : 0)
        .sort((a, b) => {
          return this._options.sort ? this.sort(a, b, value.trim()) : 0;
        });
      this.suggestions = collSuggestions.slice(0, this._options.maxItems);

      let html = this.suggestions.reduce((sentence, suggestion) => `${sentence}${item(`<span class="c-search-suggestion__title">${markText(suggestion.naam, value)}</span><span class="c-search-suggestion__subtitle">${this._options.subtitleprefix} ${markText(suggestion.relatienummer, value)}`)}`, "");

      this.ul.innerHTML = html;

      if (this.ul.children.length === 0) {
        if (this.noSuggestionsFound(value)) {
          this.open();
        } else {
          this.close({
            reason: "nomatches",
          });
        }
      } else if (collSuggestions.length > this._options.maxItems) {
        this.moreMaxSuggestionsFound(value);
        this.open();
      } else {
        this.notFound.innerHTML = '';
        this.open();
      }
    } else {
      if (this.noSuggestionsFound(value)) {
        this.open();
      } else {
        this.close({
          reason: "nomatches",
        });
      }
    }
    this._element.classList.remove("loading");
  }
}

// Private
function collectiviteit(data) {
  const o = { ...data };

  o.length = o.naam?.length ?? 0;

  o.normalizedName = o.length > 0 ? `${o.naam.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")}` : "";

  o.toString = () => `${o.naam}`;

  return o;
}

// Helpers
function markText(text, input) {
  return !input || input === "" ? text : text.replace(new RegExp(regExpEscape(input.trim()), "gi"), "<mark>$&</mark>");
}

function item(html) {
  return `<li class="c-collective__c-suggestionlist" aria-selected="false">${html}</li>`;
}

function getQueryString(name, raw = false) {
  if (!name || typeof name !== "string") {
    return "";
  }
  let params =
    location.search &&
    location.search.length > 0 &&
    location.search.indexOf(name) > -1
      ? location.search.substring(1).split("&")
      : location.hash &&
        location.hash.length > 0 &&
        location.hash.indexOf(name) > -1
      ? location.hash.substring(1).split("&")
      : [];

  for (let i = 0; i < params.length; i++) {
    params[i] = params[i].replace(/\+/g, " ");
  }

  for (let param of params) {
    const p = param.split("=");
    if (p[0]?.toLowerCase() === name.toLowerCase())
      return raw ? p[1] : decodeURIComponent(p[1]);
  }
  return "";
}

function regExpEscape(s) {
  return s.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");
}

function siblingIndex(el) {
  /* eslint-disable no-cond-assign */
  let i = 0;
  while (el.previousElementSibling) {
    el = el.previousElementSibling;
    i++;
  }
  return i;
}

export default SearchSuggestions; // as Searchsuggestions
